import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type {
  IKeytipProps,
  IOverflowSet,
  IOverflowSetItemProps,
  ITheme,
} from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import {
  DirectionalHint,
  doesElementContainFocus,
  EventGroup,
  getDocument,
  ktpTargetFromId,
  mergeOverflows,
  OverflowSet,
  sequencesToID,
} from "@fluentui/react";

import type { AppFlyoutAnchor } from "@1js/acui-button";
import type { WithKey } from "@1js/acui-common";
import { dataUniqueId, idWithSuffix, UILocation } from "@1js/acui-common";
import type { AppContextType } from "@1js/acui-context";
import { AppContext } from "@1js/acui-context";
import { ribbonExperiments } from "@1js/acui-experiments";
import type {
  MenuDefinition,
  MenuSection,
  SimpleMenuItemControlProps,
} from "@1js/acui-menu";
import { AppThemeConsumer } from "@1js/acui-theme";

import type { AppControlProps } from "../../../controls";
import type { WithSectionId } from "../../../Ribbon";
import { RibbonFlyoutAnchor } from "../../../Ribbon";
import type { RibbonControlInternalProps } from "../../Controls/RibbonControlProps";
import { overflowIdSuffix } from "../../Scaling";
import { hasPinnedCombobox } from "../../utilities/hasPinnedControl";
import {
  shouldShadowRenderMenu,
  SLRControlGroupId,
} from "../RibbonControlGroup";
import { getRibbonGroupItemsIcon } from "../RibbonControlGroup/getRibbonGroupItemsIcon";
import type { RibbonControlGroupBase } from "../RibbonControlGroup/RibbonControlGroup";
import { RibbonEnlightenedComponent } from "../RibbonEnlightenedComponent";
import type { RibbonControlGroupSLRProps } from "./RibbonControlGroupSLR.Props";
import {
  getOverflowLauncherStyles,
  getOverflowSetItemStyles,
  getRibbonControlGroupSLRClassNames,
} from "./RibbonControlGroupSLR.styles";

export class RibbonControlGroupSLR
  extends React.PureComponent<RibbonControlGroupSLRProps, {}>
  implements RibbonControlGroupBase
{
  private overflowSet = React.createRef<IOverflowSet>();
  private overflowButtonLegacy = React.createRef<AppFlyoutAnchor>();
  private overflowButton = React.createRef<HTMLButtonElement>();
  private container = React.createRef<HTMLDivElement>();
  private events: EventGroup;
  private allowMouseIn: boolean = false;

  static contextType: React.Context<AppContextType> = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public constructor(props: RibbonControlGroupSLRProps) {
    super(props);
    this.events = new EventGroup(this);
  }

  private setKeyAndKeytipsForOverflowControls(
    overflowControls: SimpleMenuItemControlProps[],
    menuSectionTitle: string = "",
    sectionId?: string
  ): IOverflowSetItemProps[] {
    const { keytipProps } = this.props;

    return overflowControls.map((item: any) => {
      const extendedItem = {
        ...item,
        key: item.id,
        menuSectionTitle,
        sectionId,
      };
      if (keytipProps && item.keytipProps) {
        const itemKeytipProps = { ...item.keytipProps };
        // Set the keytip onExecute function here, need to do this so the persisted
        // keytips can be triggered before we've mapped the items to props
        // which is done in RibbonFlyoutAnchor internally
        switch (item.type) {
          case "AppSpinButtonProps":
          case "AppComboBoxProps":
            // Override persisted input items (spin and combo box) onExecute manually.
            itemKeytipProps.onExecute = () => {
              this.inputInMenuExecute(
                itemKeytipProps.keySequences,
                keytipProps.keySequences
              );
            };
            break;
          default:
            itemKeytipProps.onExecute = () => {
              item.onExecute({});
            };
            break;
        }
        extendedItem.keytipProps = itemKeytipProps;
      }
      return extendedItem;
    });
  }

  public componentDidMount(): void {
    if (this.container.current && this.props.onFocusIn) {
      this.events.on(
        this.container.current,
        "focusin",
        this.props.onFocusIn,
        true /* useCapture */
      );
    }

    if (
      this.allowMouseIn &&
      this.container.current &&
      this.props.onMouseEnter
    ) {
      this.events.on(
        this.container.current,
        "mouseenter",
        this.props.onMouseEnter,
        true /* useCapture */
      );
    }
  }

  public componentWillUnmount(): void {
    this.events.off();
  }

  public render() {
    this.allowMouseIn = !!this.context.isExperimentalFeatureEnabled?.(
      ribbonExperiments.isDisabledTooltipsEnabled
    );

    const classNames = getRibbonControlGroupSLRClassNames(this.allowMouseIn);
    const {
      overflowControls,
      multiMenuOverflowControls,
      keytipProps,
      fastDisabled,
    } = this.props;

    let overflowItems: IOverflowSetItemProps[] = [];
    if (multiMenuOverflowControls && multiMenuOverflowControls.length !== 0) {
      // Because Overflow Set doesn't support complex menus with sub-headers, we will
      // simply flatten our structured MenuSections with an extra parameter and
      // regroup the subsections in the rendering phase
      overflowItems = multiMenuOverflowControls.reduce(
        (items, overflowMenuSection) => {
          return [
            ...items,
            ...this.setKeyAndKeytipsForOverflowControls(
              overflowMenuSection.controls,
              overflowMenuSection.title,
              overflowMenuSection.id
            ),
          ];
        },
        []
      );
    } else if (overflowControls && overflowControls.length !== 0) {
      overflowItems = this.setKeyAndKeytipsForOverflowControls(
        overflowControls,
        this.props.overflowMenuLauncherLabel,
        this.props.id
      );
    }

    return (
      <div
        className={`${classNames.root}`}
        ref={this.container}
        id={SLRControlGroupId(this.props.id)}
      >
        <OverflowSet
          componentRef={this.overflowSet}
          role={"group"}
          aria-label={this.props.groupLabel}
          items={this.getContextualTopLevelControlsWithKey(fastDisabled)}
          overflowItems={overflowItems}
          onRenderItem={this.onRenderItem}
          onRenderOverflowButton={this.onRenderOverflowButton}
          keytipSequences={keytipProps && keytipProps.keySequences}
          itemSubMenuProvider={this.itemSubMenuProvider}
          data-unique-id={dataUniqueId(this.props)}
          styles={getOverflowSetItemStyles()}
        />
      </div>
    );
  }

  private getContextualTopLevelControlsWithKey = (
    fastDisabled?: boolean | (() => boolean | undefined)
  ) => {
    const topLevelControlsWithKey =
      this.props.topLevelControls &&
      this.props.topLevelControls.map((item, index, controls) => {
        return {
          ...item,
          hideLabel: "hideLabel" in item ? item.hideLabel : undefined,
          icon: getRibbonGroupItemsIcon(item),
          key: item.id,
          fastDisabledCallback:
            typeof fastDisabled === "function" ? fastDisabled : undefined,
          shadowRenderMenu: shouldShadowRenderMenu(
            `shouldShadowRenderFn-${item.id}${item.location || ""}-SLR`,
            typeof this.props.fastDisabled === "function"
              ? this.props.fastDisabled?.()
              : undefined,
            // We can't "&&" preventShadowRendering with shadowRenderMenu
            // because any "&&" with "undefined" will always be "undefined"
            !!this.props.preventShadowRendering
              ? false
              : (item as any).shadowRenderMenu
          ),
          ...hasPinnedCombobox(controls, index),
          keytipProps: (item as any)?.keytipProps,
        };
      });
    return topLevelControlsWithKey;
  };

  public focus() {
    if (this.overflowSet.current) {
      this.overflowSet.current.focus(
        true /* forceIntoFirstChild */,
        true /* bypassHiddenElements */
      );
    }
  }

  public hasContainer(): boolean {
    return (
      !!this.container.current && this.container.current.offsetHeight !== 0
    );
  }

  public containsFocus(): boolean {
    if (this.hasContainer()) {
      return doesElementContainFocus(this.container.current as HTMLElement);
    }

    return false;
  }

  private onRenderOverflowButton = (overflowItems: any[]) => {
    const content: JSX.Element = (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <RibbonFlyoutAnchor
            ref={this.overflowButton}
            legacyRef={this.overflowButtonLegacy}
            type="AppFlyoutAnchorProps"
            id={idWithSuffix(this.props.id, overflowIdSuffix)!}
            variant="overflowButton"
            icon={this.props.overflowMenuLauncherIcon}
            menuDefinition={
              this.props.deferredOverflowMenuAs
                ? undefined
                : constructMenuDefinitionForOverflow(overflowItems)
            }
            directionalHint={DirectionalHint.bottomLeftEdge}
            label={this.props.overflowMenuLauncherLabel}
            disabled={this.props.overflowMenuLauncherDisabled}
            hideLabel={true}
            hideChevron={true}
            styles={getOverflowLauncherStyles(theme)}
            onExecute={this.props.onOverflowMenuOpen}
            location={UILocation.Overflow}
            onAfterMenuDismiss={this.props.onOverflowMenuClose}
            keytipProps={this.props.keytipProps}
            controlInMenuRendererFunction={
              this.props.controlInMenuRendererFunction
            }
            downChevronIcon={this.props.overflowMenuLauncherIcon}
            deferredMenuDefinition={
              this.props.deferredOverflowMenuAs
                ? {
                    type: "DeferredOverflowMenuDefinition",
                    menuAs: this.props.deferredOverflowMenuAs,
                    overflowMenuDefinition:
                      constructMenuDefinitionForOverflow(overflowItems),
                    parentKeytipSequence: this.props.keytipProps?.keySequences,
                    anchorId: this.props.id,
                  }
                : undefined
            }
            preventMenuExecution={this.props.preventMenuExecution}
            shadowRenderMenu={
              this.props.preventShadowRendering ? false : undefined
            }
            showTooltip={true}
          />
        )}
      </AppThemeConsumer>
    );

    return this.props.isRibbonSpanning ? (
      <RibbonEnlightenedComponent
        id={this.props.overflowMenuLauncherLabel}
        key={this.props.overflowMenuLauncherLabel}
        offset={
          this.props.spanningComponentId ===
          this.props.overflowMenuLauncherLabel
            ? this.props.spanningOffset
            : 0
        }
      >
        {content}
      </RibbonEnlightenedComponent>
    ) : (
      content
    );
  };

  private onRenderItem = (item: IOverflowSetItemProps) => {
    const controlProps = {
      ...(item as WithKey<AppControlProps>),
      animate: this.props.animate,
    } as WithKey<RibbonControlInternalProps>;

    const content: JSX.Element =
      this.props.controlRendererFunction(controlProps);

    return this.props.isRibbonSpanning ? (
      <RibbonEnlightenedComponent
        id={controlProps.key}
        key={controlProps.key}
        offset={
          this.props.spanningComponentId === controlProps.key
            ? this.props.spanningOffset
            : 0
        }
      >
        {content}
      </RibbonEnlightenedComponent>
    ) : (
      content
    );
  };

  /**
   * itemSubMenuProvider needed for keytips integration
   * The items we pass to our OverflowSet have not yet been converted to props (RibbonFlyoutAnchor does this internally)
   * This lets the OverflowSet know if an item has a submenu or not
   */
  private itemSubMenuProvider = (
    item: IOverflowSetItemProps
  ): any[] | boolean | undefined => {
    const menuDefinition: MenuDefinition = (item as any).menuDefinition;
    const deferredMenuDefinition = (item as any).deferredMenuDefinition;
    const keytipProps: IKeytipProps | undefined = (item as any).keytipProps;
    return (
      !!menuDefinition ||
      !!deferredMenuDefinition ||
      (keytipProps && !!keytipProps.hasMenu)
    );
  };

  /**
   * Persisted keytip for input items callback.
   * Should open the overflow menu and focus the relevant item.
   * We need to override the default focusing in BaseButton openMenu so we can focus the item we want.
   */
  private inputInMenuExecute = (
    keySequences: string[],
    overflowKeySequences: string[]
  ) => {
    if (this.overflowButtonLegacy && this.overflowButtonLegacy.current) {
      this.overflowButtonLegacy.current.focus();
      this.overflowButtonLegacy.current.openMenu(false, false);
      // Find and focus the relevant input element manually
      // Won't be passed as an onExecute param because it doesn't exist until the menu is opened
      const seqWithOverflow = mergeOverflows(
        keySequences,
        overflowKeySequences
      );
      const inputEl = getDocument()!.querySelector(
        ktpTargetFromId(sequencesToID(seqWithOverflow))
      ) as HTMLElement;
      inputEl && inputEl.focus();
    }
  };
}

export function constructMenuDefinitionForOverflow(
  overflowItems: any[]
): MenuDefinition | undefined {
  const menuSectionsByKey: {
    [s: string]: WithSectionId<AppControlProps>[];
  } = {};
  overflowItems.forEach(({ menuSectionTitle, ...overflowItem }) => {
    menuSectionsByKey[menuSectionTitle] =
      menuSectionsByKey[menuSectionTitle] || [];
    menuSectionsByKey[menuSectionTitle].push({
      ...overflowItem,
      icon: getRibbonGroupItemsIcon(overflowItem),
    });
  });
  const menuSections: MenuSection[] = Object.keys(menuSectionsByKey).map(
    (sectionTitle) => {
      const section: MenuSection = {
        title: sectionTitle,
        controls: menuSectionsByKey[sectionTitle],
      };
      // Set the section's id if we were provided with a sectionId
      if (menuSectionsByKey[sectionTitle][0]?.sectionId) {
        section.id = menuSectionsByKey[sectionTitle][0]?.sectionId;
      }
      return section;
    }
  );

  return menuSections.length === 0
    ? undefined
    : {
        sections: menuSections,
      };
}
